import { type MouseEvent, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import type { EnhancedRun } from "src/business-components/input/PitSelector/PitSelector.types";
import { useBuildLink } from "src/hooks/useBuildLink";
import { useChangePit } from "src/hooks/useChangePit";
import { useQueryRuns } from "src/queries/pipelines/pipelineRuns.query";
import { useQueryMinimalTasksRuns } from "src/queries/tasks/tasks.query";
import type { MinimalTaskRun } from "src/queries/tasks/tasks.types";
import type {
  AppPath,
  PitPath,
  TaskPath,
  TaskRedirectPath,
  TaskRunPath,
} from "src/utils/appRoutes/appRoutes.types";
import { formatPitForDisplay } from "src/utils/time";
import type {
  PipelinePitSelectorLocationState,
  PipelinePitSelectorPits,
  PipelinePitSelectorRun,
} from "./PipelinePitSelector.types";
import { groupTaskRunsByPit } from "./PipelinePitSelector.utils";

/**
 * Returns the pits for a specific task or all the pipeline's pits
 */
export const useGetTaskPits = (
  params: {
    body?: {
      task_name?: string;
      [any: string]: string | number | boolean | undefined;
    };
    options?: {
      enabled?: boolean;
    };
  } = {}
): {
  filteredPits: PipelinePitSelectorPits;
  isLoading: boolean;
} => {
  const hasTaskName = !!params.body?.task_name;
  params.body = params.body ?? {};

  const tasksRunsQuery = useQueryMinimalTasksRuns({
    params: params.body,
    caller: "PipelinePitSelector.useGetTaskPits",
    ...(params.options?.enabled ? { enabled: true } : {}),
  });

  // should only be called in a "task" or "task-run" page
  const { data: appPits } = useQueryRuns({
    // enabled: !hasTaskName,
    params: params.body,
    caller: "PipelinePitSelector.useGetTaskPits",
    enabled: params.options?.enabled !== false,
  });

  const filteredPits: PipelinePitSelectorPits = useMemo(
    () =>
      hasTaskName ? groupTaskRunsByPit(tasksRunsQuery.data ?? []) : (appPits ?? []),
    [tasksRunsQuery.data, hasTaskName, appPits]
  );

  return { filteredPits, isLoading: tasksRunsQuery.isLoading };
};

export const usePipelinePitSelector = (task_name?: string) => {
  const urlParams = useParams();
  const navigate = useNavigate();
  const buildLink = useBuildLink();
  const changePit = useChangePit();
  const [searchParams] = useSearchParams();
  const { filteredPits: appPits } = useGetTaskPits(
    task_name ? { body: { task_name } } : undefined
  );

  const onClickRenderOption = (
    e: MouseEvent<HTMLLIElement>,
    option: PipelinePitSelectorRun,
    opts?: { viewTransition?: boolean }
  ) => {
    if ((option as EnhancedRun).isRerun || (option as MinimalTaskRun).task_id) {
      if (!urlParams.app_name) {
        return;
      }

      const { task_id, task_name: optionTaskName } = option as MinimalTaskRun;
      const search = searchParams.toString();
      const app_pit = formatPitForDisplay(option.app_pit);
      const url = buildLink(
        task_id
          ? ({
              app_name: urlParams.app_name,
              app_pit,
              task_id,
              search,
            } as TaskRunPath)
          : ({ app_name: urlParams.app_name, app_pit, search } as PitPath)
      );
      // Task-run URLs have no task_name. Carry it so the next page can use the cached list immediately.
      const state: PipelinePitSelectorLocationState | undefined =
        task_id && optionTaskName ? { task_name: optionTaskName } : undefined;

      url && void navigate(url, { viewTransition: opts?.viewTransition, state });
    } else {
      changePit({
        pit: option.app_pit,
        newTab: e?.metaKey,
        viewTransition: opts?.viewTransition,
      });
    }
  };

  // const task_name = params.task_name || taskInfoQuery.data?.task_name;

  const redirectWithoutPIT = () => {
    if (urlParams.app_name) {
      const search = window.location.search;
      const path = buildLink(
        urlParams.task_id
          ? ({
              task_id: Number(urlParams.task_id),
              redirect: true,
            } as TaskRedirectPath)
          : task_name
            ? ({ app_name: urlParams.app_name, task_name, search } as TaskPath)
            : ({ app_name: urlParams.app_name, search } as AppPath)
      );

      path && void navigate(path);
    }
  };

  return {
    redirectWithoutPIT,
    onClickRenderOption,
    appPits,
  };
};
